import { downloadSheet, postForm, postJson } from "./http";

/** Customers and vendors: the book, the corrections queue, and the bulk sheet. */

export type Party = "customer" | "vendor";

/** The exception kinds, in the order the screen groups them. */
export type ContactIssueKind =
  | "missing-gstin"
  | "invalid-gstin"
  | "gst-treatment-mismatch"
  | "missing-state"
  | "state-gstin-mismatch"
  | "pan-mismatch"
  | "vendor-missing-tds"
  | "msme-missing-udyam"
  | "duplicate-gstin"
  | "duplicate-name"
  | "both-party";

export interface ContactIssueDTO {
  kind: ContactIssueKind;
  /** Which field an inline fix should edit. */
  field: string;
  message: string;
  /**
   * A value we can defend rather than guess — the state read out of the GSTIN's
   * first two digits, or the PAN read out of its middle. Absent when there is
   * nothing to propose, which is most kinds.
   */
  suggestion?: string;
  /** `{contact_id}:{kind}` — what the ignore endpoint takes. */
  key: string;
  ignored: boolean;
  ignored_reason: string | null;
}

/** Field-level before and after for a correction that has not been sent yet. */
export type QueuedChanges = Record<string, { from: string | null; to: string | null }>;

export interface ContactDTO {
  contact_id: string;
  contact_name: string | null;
  company_name: string | null;
  contact_type: Party | null;
  status: string | null;
  gst_treatment: string | null;
  gst_no: string | null;
  pan_no: string | null;
  /** The CODE Zoho stores ("MH"). */
  place_of_contact: string | null;
  place_of_contact_label: string | null;
  currency_code: string | null;
  payment_terms: number | null;
  email: string | null;
  phone: string | null;
  tds_tax_id: string | null;
  tds_tax_name: string | null;
  msme_type: string | null;
  udyam_reg_no: string | null;
  /**
   * Null means the detail sweep has not reached this contact. Every field from
   * `tds_tax_id` down is meaningless until it is set — a vendor showing no TDS
   * section before this is a vendor nobody has looked at, not a vendor with none.
   */
  details_fetched_at: string | null;
  has_transaction: boolean;
  /** Local-only fields the sheet collected. Null when there are none. */
  extras: Record<string, string> | null;
  /** Fields with a correction queued against them. */
  queued: QueuedChanges | null;
  issues: ContactIssueDTO[];
}

export interface QueuedEditDTO {
  contact_id: string;
  contact_name: string | null;
  changes: QueuedChanges;
  status: "pending" | "failed";
  /** Zoho's own words, when it refused this one. */
  error: string | null;
}

export interface ReadbackStateDTO {
  status: string;
  row_count: number;
  fetched_at: string | null;
  error: string | null;
}

export interface TdsRateDTO {
  tax_id: string;
  tax_name: string;
  rate: string | number | null;
}

export interface ContactBookDTO {
  contacts: ContactDTO[];
  counts: { customers: number; vendors: number };
  /**
   * Contacts whose detail has not been read yet. Above zero means the TDS and
   * MSME exceptions are still incomplete, and the screen has to say so rather
   * than quietly under-reporting.
   */
  pending_detail: number;
  edits: QueuedEditDTO[];
  issue_kinds: ContactIssueKind[];
  labels: Record<ContactIssueKind, string>;
  /** Label => Zoho code, for the place-of-supply picker. */
  states: Record<string, string>;
  /** Label => Zoho code, for the GST treatment picker. */
  treatments: Record<string, string>;
  tds_rates: TdsRateDTO[];
  readback: {
    contacts: ReadbackStateDTO | null;
    contact_details: ReadbackStateDTO | null;
  };
  /** Only on a commit response. */
  push?: EditPushDTO;
}

/** What one call to the corrections commit did. */
export interface EditPushDTO {
  pushed: number;
  failed: number;
  /**
   * Corrections still to send. Refused ones are NOT counted — they would fail
   * the same way and keep the caller's loop running against a wall.
   */
  remaining: number;
  halted: boolean;
  halted_reason: string | null;
}

// ── The bulk sheet ────────────────────────────────────────────────────────

export type ContactImportAction = "new" | "update" | "unchanged" | "invalid";

export type ContactImportRowStatus = "pending" | "created" | "updated" | "skipped" | "failed";

export interface ContactImportIssue {
  /** The column heading, so a message points at a cell. */
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface ContactImportRowDTO {
  id: number;
  /** The line in the CA's own spreadsheet, not our ordering. */
  sheet_row_no: number;
  zoho_contact_id: string | null;
  contact_name: string | null;
  gst_no: string | null;
  action: ContactImportAction;
  status: ContactImportRowStatus;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  issues: ContactImportIssue[];
  error: string | null;
  /** The sheet's own cells, so a row can be shown the way its author typed it. */
  raw: Record<string, string | null>;
}

export type ContactImportStatus = "validated" | "committing" | "done" | "cancelled";

export interface ContactImportDTO {
  id: number;
  party: Party;
  original_filename: string;
  status: ContactImportStatus;
  total_rows: number;
  new_rows: number;
  changed_rows: number;
  unchanged_rows: number;
  invalid_rows: number;
  created_rows: number;
  updated_rows: number;
  failed_rows: number;
  created_at: string | null;
  committed_at: string | null;
  /**
   * This exact file has been pushed before. Not a block — re-running after a
   * partial failure is legitimate — but Zoho has no idempotency key, so a blind
   * re-push would create every new contact on it a second time.
   */
  already_pushed_at: string | null;
}

export interface ContactPushDTO {
  status: "committing" | "done";
  /**
   * The run stopped for a reason that will still be true if called again
   * straight away — a rate limit, or refused credentials. The caller must NOT
   * loop on this.
   */
  halted: boolean;
  halted_reason: string | null;
  attempted: number;
  remaining: number;
  created: number;
  updated: number;
  failed: number;
}

/** `import` is null whenever there is nothing open, which is the ordinary state. */
export interface ContactImportReviewDTO {
  import: ContactImportDTO | null;
  rows: ContactImportRowDTO[];
  push?: ContactPushDTO;
}

// ── Transport ─────────────────────────────────────────────────────────────

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// The server puts the readable reason under a fixed key — "the check
// character should be K, not J" rather than "HTTP 400". `public_id` is in the
// list because the import endpoints refuse under that key ("this sheet is
// part-way into the client's books"), and without it those sentences reached
// the screen as the envelope's generic "Validation failed".
const contactsCall = <T>(path: string, body: unknown, errorKey = "contact"): Promise<T> =>
  postJson<T>(path, body, [errorKey, "commit", "public_id"]);

// ── The book ──────────────────────────────────────────────────────────────

/** Everything the step renders, in one read. */
export const fetchContactBook = async (publicId: string): Promise<ContactBookDTO> =>
  (await contactsCall<Envelope<ContactBookDTO>>("/api/onboarding/contacts/book", { public_id: publicId })).data;

/**
 * Queue one correction. NOTHING IS SENT.
 *
 * Validated server-side against the same rules the spreadsheet uses, so a fix
 * that passes here will not be refused by the sheet path later. Returns the
 * whole book so the exception it fixes disappears in the same render.
 */
export const queueContactEdit = async (
  publicId: string,
  contactId: string,
  changes: Record<string, string | null>
): Promise<ContactBookDTO> =>
  (
    await contactsCall<Envelope<ContactBookDTO>>("/api/onboarding/contacts/edits/queue", {
      public_id: publicId,
      contact_id: contactId,
      changes,
    })
  ).data;

/** Drop one queued correction, or the whole queue when no contact is named. */
export const discardContactEdit = async (
  publicId: string,
  contactId?: string
): Promise<ContactBookDTO> =>
  (
    await contactsCall<Envelope<ContactBookDTO>>("/api/onboarding/contacts/edits/discard", {
      public_id: publicId,
      contact_id: contactId,
    })
  ).data;

/**
 * Send the queued corrections to the client's Zoho Books.
 *
 * THIS WRITES. One batch per call; the caller comes back while `push.remaining`
 * is above zero and `push.halted` is false. Updates rather than creates, so
 * unlike the sheet push there is no duplicate to guard against.
 */
export const commitContactEdits = async (publicId: string): Promise<ContactBookDTO> =>
  (await contactsCall<Envelope<ContactBookDTO>>("/api/onboarding/contacts/edits/commit", { public_id: publicId })).data;

/**
 * Set an exception aside with a reason, or bring it back by passing none.
 *
 * Records a decision about a client's books, never a change to them.
 */
export const setContactIssueIgnored = async (
  publicId: string,
  key: string,
  reason: string | null
): Promise<ContactBookDTO> =>
  (
    await contactsCall<Envelope<ContactBookDTO>>("/api/onboarding/contacts/ignore", {
      public_id: publicId,
      key,
      reason,
    })
  ).data;

// ── The bulk sheet ────────────────────────────────────────────────────────

/**
 * The client's customers, or vendors, as a spreadsheet saved to their machine.
 *
 * A POST returning a blob rather than a link, because the endpoint needs the
 * bearer token and an `<a href>` cannot carry one. The object URL is revoked
 * straight after; without that the whole file stays in memory until the tab
 * closes.
 */
export const downloadContactTemplate = (publicId: string, party: Party): Promise<void> =>
  downloadSheet("/api/onboarding/contacts/template", { public_id: publicId, party }, `${party}s.xlsx`);

/**
 * Send the filled-in sheet up and get back what it would change.
 *
 * The server puts the readable refusal under errors.file — "that sheet has no
 * header row" rather than "HTTP 400".
 */
export async function uploadContactSheet(
  publicId: string,
  party: Party,
  file: File
): Promise<ContactImportReviewDTO> {
  const form = new FormData();
  form.append("public_id", publicId);
  form.append("party", party);
  form.append("file", file);

  return (await postForm<Envelope<ContactImportReviewDTO>>("/api/onboarding/contacts/import/upload", form, ["file"])).data;
}

/** The review as it stands. Survives a reload; returns `{import: null}` when there is none. */
export const fetchContactImport = async (
  publicId: string,
  party: Party
): Promise<ContactImportReviewDTO> =>
  (
    await contactsCall<Envelope<ContactImportReviewDTO>>(
      "/api/onboarding/contacts/import/get",
      { public_id: publicId, party },
      "file"
    )
  ).data;

/** Throw the review away and delete the uploaded file. */
export const cancelContactImport = async (
  publicId: string,
  party: Party
): Promise<ContactImportReviewDTO> =>
  (
    await contactsCall<Envelope<ContactImportReviewDTO>>(
      "/api/onboarding/contacts/import/cancel",
      { public_id: publicId, party },
      "file"
    )
  ).data;

/**
 * Send one batch of the reviewed sheet into the client's Zoho Books.
 *
 * THIS WRITES AND NOTHING IT DOES CAN BE UNDONE. Zoho has no bulk create for
 * contacts, so a batch is a handful of individual writes; the caller keeps
 * calling while `push.status` is `committing` — but stops on `push.halted`,
 * which means calling again now would just fail the same way.
 *
 * `acknowledgeDuplicate` corresponds to the CA having been shown that this exact
 * file was pushed before and choosing to send it anyway. The server refuses
 * without it, so it is a decision rather than a default.
 */
export const commitContactImport = async (
  publicId: string,
  party: Party,
  acknowledgeDuplicate = false
): Promise<ContactImportReviewDTO> =>
  (
    await contactsCall<Envelope<ContactImportReviewDTO>>(
      "/api/onboarding/contacts/import/commit",
      { public_id: publicId, party, acknowledge_duplicate: acknowledgeDuplicate },
      "commit"
    )
  ).data;

/** Dismiss a finished push. Not an undo — the contacts are in Zoho. */
export const closeContactImport = async (
  publicId: string,
  party: Party
): Promise<ContactImportReviewDTO> =>
  (
    await contactsCall<Envelope<ContactImportReviewDTO>>(
      "/api/onboarding/contacts/import/close",
      { public_id: publicId, party },
      "file"
    )
  ).data;
